import React, { useEffect, useState } from 'react';
import { Box, Group, Flex, Button, Burger, Drawer, Divider, Stack, Title, Container } from '@mantine/core';
import { useDisclosure, useViewportSize } from '@mantine/hooks';
import { rem } from '@mantine/core';
import classes from './HeaderMegaMenu.module.css';

interface HeaderProps {
    onLoginClick: () => void;
    onRegisterClick: () => void;
    scrollToSection?: (sectionId: string) => void;
}

export const Header: React.FC<HeaderProps> = ({ onLoginClick, onRegisterClick, scrollToSection }) => {
    const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
    const { width } = useViewportSize();
    const [scrolled, setScrolled] = useState(false);

    // Track scrolling to add shadow only when scrolled
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Add scroll animation for navigation links
    const handleScrollToSection = (elementId: string) => {
        closeDrawer();
        const element = document.getElementById(elementId);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    // Dynamic sizing for small screens
    const isMobile = width < 768;
    const isTablet = width >= 768 && width < 1100;

    // Adjust logo and title size based on screen size
    const logoHeight = isMobile ? 36 : isTablet ? 46 : 52;
    const titleFontSize = isMobile ? 24 : isTablet ? 32 : 40;
    const linkFontSize = isMobile ? 18 : 18;
    const buttonSize = isMobile ? "sm" : isTablet ? "sm" : "md";

    return (
        <Box>
            <header
                className={classes.header}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    padding: isMobile ? '15px 10px' : isTablet ? '16px 20px' : '25px 50px 20px',
                    background: '#ffffff',
                    boxShadow: scrolled ? '0px 2px 10px rgba(0, 0, 0, 0.1)' : 'none',
                    transition: 'box-shadow 0.3s ease',
                    height: isMobile ? '70px' : isTablet ? '85px' : '100px'
                }}
            >
                <Container size="xl" p={0} style={{ maxWidth: '100%' }}>
                    <Flex justify="space-between" align="center" h="100%" wrap="nowrap" gap={isMobile ? 'xs' : 'md'}>
                        {/* Left section - Logo */}
                        <Flex align="center" style={{ flexShrink: 1, minWidth: 0 }}>
                            <a 
                                href="/"
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    textDecoration: 'none',
                                    color: 'inherit'
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    window.location.href = '/';
                                }}
                            >
                                <img
                                    src="/logo.png"
                                    alt="Company Logo"
                                    style={{
                                        height: logoHeight,
                                        marginRight: isMobile ? 8 : 16,
                                        marginTop: isMobile ? -5 : -15,
                                        marginLeft: isMobile ? 5 : 0,
                                        cursor: 'pointer'
                                    }}
                                />
                                <Title
                                    c="black"
                                    order={1}
                                    style={{
                                        fontFamily: 'Bebas Neue',
                                        fontSize: titleFontSize,
                                        whiteSpace: 'nowrap',
                                        letterSpacing: isMobile ? '0.5px' : 'normal',
                                        cursor: 'pointer'
                                    }}
                                >
                                    SORT MY SHOEBOX
                                </Title>
                            </a>
                        </Flex>

                        {/* Center section - Navigation links (desktop only) */}
                        <Flex
                            justify="center"
                            align="center"
                            style={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: isMobile || isTablet ? 'none' : 'flex',
                            }}
                        >
                            <Group h="100%" gap="xl">
                                <a
                                    href="#steps"
                                    className={classes.link}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        scrollToSection ? scrollToSection('steps') : handleScrollToSection('steps');
                                    }}
                                    style={{ fontSize: rem(linkFontSize) }}
                                >
                                    How It Works
                                </a>
                                <a
                                    href="#features"
                                    className={classes.link}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        scrollToSection ? scrollToSection('features') : handleScrollToSection('features');
                                    }}
                                    style={{ fontSize: rem(linkFontSize) }}
                                >
                                    Features
                                </a>
                                <a
                                    href="#pricing"
                                    className={classes.link}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        scrollToSection ? scrollToSection('pricing') : handleScrollToSection('pricing');
                                    }}
                                    style={{ fontSize: rem(linkFontSize) }}
                                >
                                    Pricing
                                </a>

                            </Group>
                        </Flex>

                        {/* Right section - Buttons */}
                        <Flex justify="flex-end" align="center" style={{ flexShrink: 0 }}>
                            <Group gap={isMobile ? 'xs' : 'md'} visibleFrom="sm">
                                <Button
                                    variant="default"
                                    onClick={onLoginClick}
                                    size={buttonSize}
                                    style={{ padding: isMobile ? '0 10px' : undefined }}
                                    component="a"
                                    href="/login"
                                >
                                    Log in
                                </Button>
                                <Button
                                    onClick={onRegisterClick}
                                    size={buttonSize}
                                    style={{ padding: isMobile ? '0 10px' : undefined }}
                                    component="a"
                                    href="/signup"
                                >
                                    Sign Up
                                </Button>
                            </Group>

                            <Burger
                                opened={drawerOpened}
                                onClick={toggleDrawer}
                                hiddenFrom="sm"
                                size={isMobile ? "sm" : "md"}
                                ml="xs"
                            />
                        </Flex>
                    </Flex>
                </Container>
            </header>

            {/* Improved dropdown drawer */}
            <Drawer
                opened={drawerOpened}
                onClose={closeDrawer}
                size={isMobile ? "100%" : "50%"}
                position="top"
                withCloseButton
                styles={{
                    body: {
                        padding: isMobile ? '15px 20px' : '20px',
                    },
                    content: {
                        boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                        borderRadius: '0 0 15px 15px'
                    }
                }}
            >
                <Stack gap={isMobile ? "lg" : "xl"} align="center" style={{ fontSize: rem(isMobile ? 18 : 20) }}>
                    <a
                        href="#steps"
                        className={classes.link}
                        onClick={(e) => {
                            e.preventDefault();
                            scrollToSection ? scrollToSection('steps') : handleScrollToSection('steps');
                        }}
                    >
                        How It Works
                    </a>
                    <a
                        href="#features"
                        className={classes.link}
                        onClick={(e) => {
                            e.preventDefault();
                            scrollToSection ? scrollToSection('features') : handleScrollToSection('features');
                        }}
                    >
                        Features
                    </a>
                    <a
                        href="#pricing"
                        className={classes.link}
                        onClick={(e) => {
                            e.preventDefault();
                            scrollToSection ? scrollToSection('pricing') : handleScrollToSection('pricing');
                        }}
                    >
                        Pricing
                    </a>
                </Stack>

                <Divider my="xl" />

                <Flex direction="column" gap="md" mt={isMobile ? "lg" : "xl"}>
                    <Button 
                        onClick={() => { onLoginClick(); closeDrawer(); }} 
                        fullWidth 
                        size={isMobile ? "lg" : "md"} 
                        style={{ height: isMobile ? '48px' : undefined }}
                        component="a"
                        href="/login"
                    >
                        Log in
                    </Button>
                    <Button 
                        onClick={() => { onRegisterClick(); closeDrawer(); }} 
                        fullWidth 
                        size={isMobile ? "lg" : "md"} 
                        style={{ height: isMobile ? '48px' : undefined }}
                        component="a"
                        href="/signup"
                    >
                        Sign Up
                    </Button>
                </Flex>
            </Drawer>
        </Box>
    );
};